export interface MergeObjectOptions {
  changes?: string[];
  scope?: string[];
  mergeBlacklist?: Object[];
  clone?: boolean;
}

export default function mergeObject<
  O extends object,
  S extends object
>(object: O, source: S, { changes, mergeBlacklist = [], scope = [], clone = true }: MergeObjectOptions = {}) {
  if (!source) {
    return object;
  }

  if (!object) {
    if (changes) {
      changes.push('.');
    }
    return source;
  }

  if (mergeBlacklist.includes(source)) {
    if (changes) {
      changes.push('.');
    }
    return source;
  }

  const keys = Object.keys(source);

  if (!keys.length) {
    return object;
  }

  const initialValue = (() => {
    if (clone === false) {
      return object;
    }

    if (Array.isArray(object)) {
      return object.concat();
    }

    return { ...object };
  })();

  return keys.reduce<{[index: string]: unknown}>((mergedObject, key) => {
    const oldValue = (object as { [index: string]: unknown })[key];
    const newValue = (source as { [index: string]: unknown })[key];

    const shouldMergeDeeper = (
      oldValue !== newValue &&
      oldValue !== null &&
      newValue !== null &&
      typeof oldValue === 'object' &&
      typeof newValue === 'object' &&
      !mergeBlacklist.includes(newValue)
    );

    if (shouldMergeDeeper) {
      const newScope = [...scope, key];
      mergedObject[key] = mergeObject(oldValue as {[index: string]: unknown}, newValue as {[index: string]: unknown}, { changes, mergeBlacklist, scope: newScope });
    } else {
      if (changes) {
        changes.push([...scope, key].join('.'));
      }

      mergedObject[key] = newValue;
    }

    return mergedObject;
  }, initialValue as {[index: string]: unknown});
}
